import { v4 } from 'uuid';
import { takeEvery, all, put } from 'redux-saga/effects';

export const createFlowType = type => ({
  request: `${type}/REQUEST`,
  success: `${type}/SUCCESS`,
  fail: `${type}/FAIL`,
  setData: `${type}/SET_DATA`,
  setProps: `${type}/SET_PROPS`,
});

export class Flow {
  constructor(name, initialData = {}) {
    this.name = name;
    this.type = createFlowType(`${v4()}/${name}`);
    this.initialData = initialData;
  }

  getName = () => this.name;

  request = (props = {}) => ({
    ...props,
    type: this.type.request,
  });

  success = payload => ({
    type: this.type.success,
    payload,
  });

  fail = (error, formattedError) => ({
    type: this.type.fail,
    error, formattedError,
  });

  setData = payload => ({
    type: this.type.setData,
    payload,
  });

  setProps = payload => ({
    type: this.type.setProps,
    payload,
  });

  reducerRequest = state => ({
    ...state,
    [this.name]: {
      ...state[this.name],
      data: this.initialData,
      fetching: true,
      error: null,
      formattedError: null,
    },
  });

  reducerSuccess = (state, action) => ({
    ...state,
    [this.name]: {
      ...state[this.name],
      data: action.payload,
      fetching: false,
      error: null,
      formattedError: null,
    },
  });

  reducerFail = (state, action) => ({
    ...state,
    [this.name]: {
      ...state[this.name],
      data: this.initialData,
      fetching: false,
      error: action.error,
      formattedError: action.formattedError,
    },
  });

  reducerSetData = (state, action) => ({
    ...state,
    [this.name]: {
      ...state[this.name],
      data: action.payload,
    },
  });

  reducerSetProps = (state, action) => ({
    ...state,
    [this.name]: {
      ...state[this.name],
      ...action.payload,
    },
  });

  getInitialState = () => ({
    [this.name]: {
      data: this.initialData,
      fetching: false,
      error: null,
    },
  });

  getReducers = () => ({
    [this.type.request]: this.reducerRequest,
    [this.type.success]: this.reducerSuccess,
    [this.type.fail]: this.reducerFail,
    [this.type.setData]: this.reducerSetData,
    [this.type.setProps]: this.reducerSetProps,
  });

  getSaga = () => {
    const self = this;

    function* fetch(action) {
      try {
        const payload = self.onFetch && typeof self.onFetch === 'function'
        ? yield (self.onFetch(action))
        : undefined;

        yield put(self.success(payload || self.initialData));
      } catch (error) {
        const payload = [];

        if (typeof error === 'string') {
          payload.push(error);
        }

        if (error.message) {
          payload.push(error.message);
        }

        if (error.response) {
          payload.push(error.response.data);
        }

        let formattedError = payload.join(' \n ');

        if (self.formatError) {
          formattedError = yield self.formatError(error, formattedError);
        }

        if (self.onFail && typeof self.onFail === 'function') {
          const fError = yield self.onFail(action, error, formattedError);

          if (fError !== undefined) {
            formattedError = fError;
          }
        }

        console.error(error);

        // ToDo add auditLog here

        yield put(self.fail(error, formattedError));
      } finally {
        if (self.onDone && typeof self.onDone === 'function') {
          yield self.onDone(action);
        }
      }
    }

    function* watch() {
      yield takeEvery(self.type.request, fetch);
    }

    return watch();
  };

  onFetch = () => {};

  setActions = ({ onFetch, onFail, formatError, onDone }) => {
    this.onFetch = onFetch;
    this.onFail = onFail;
    this.formatError = formatError;
    this.onDone = onDone;
  };
}

export class FlowList {
  flows = {};
  sagas = [];
  initialState = {};

  constructor(...flows) {
    this.flows = flows.reduce((prev, flow) => ({
      ...prev,
      [flow.getName()]: flow,
    }), {});
  }

  getFlow = name => this.flows[name];

  getFlows = () => this.flows;

  getInitialState = () => Object.keys(this.flows).reduce((prev, key) => ({
    ...prev,
    ...this.flows[key].getInitialState(),
  }), this.initialState);

  mergeInitialState = initialState => this.initialState = initialState;

  getReducers = () => Object.keys(this.flows).reduce((prev, key) => ({
    ...prev,
    ...this.flows[key].getReducers(),
  }), {});

  watch = (type, saga) => {
    this.sagas.push({
      watch: () => {
        function* gen() {
          return yield takeEvery(type, saga);
        }

        return gen();
      },
    });
  };

  getSagas = () => {
    const self = this;

    return function* root() {
      yield all([
        ...Object.keys(self.flows).map(key => self.flows[key].getSaga()),
        ...self.sagas.map(item => item.watch()),
      ]);
    };
  };
}

export default Flow;
